import { useCallback, useEffect, useMemo, useState, type CSSProperties } from "react";
import { useTranslation } from "react-i18next";
import { mailApi, type Communication, type CommunicationAttachment } from "../../api/mail";
import type { ApiError } from "../../api/client";
import Ic from "../icons.jsx";

/**
 * Chronological correspondence for one lead or one customer company
 * (GET /api/communications). Sent and received messages are visually distinct:
 * an outgoing row is tinted with the brand colour and reads left-anchored on a
 * blue rail, an incoming row uses the "ok" green rail — so a glance down the
 * rail answers "who spoke last?" without reading a word.
 *
 * Bodies are excerpted (the ingest stores full MIME text) and expand in place;
 * attachments render as chips with a human size. The row is deliberately NOT a
 * mail client: no reply-in-place, no folders — deliberately out of scope.
 */
export type CommunicationTimelineProps = {
  leadId?: string | null;
  companyId?: string | null;
  /** Bump to force a refetch — e.g. after SendMailModal reports a sent message. */
  reloadToken?: number;
};

const EXCERPT_CHARS = 280;

/**
 * Opens a stored attachment. The URL is presigned server-side and lives ~5
 * minutes, so it is fetched on click rather than embedded in the list.
 */
async function openAttachment(communicationId: string, attachmentId: string) {
  const { url } = await mailApi.attachmentUrl(communicationId, attachmentId);
  window.open(url, "_blank", "noopener,noreferrer");
}

export function CommunicationTimeline({ leadId, companyId, reloadToken = 0 }: CommunicationTimelineProps) {
  const { t, i18n } = useTranslation();
  const locale = i18n.resolvedLanguage ?? "en";

  const [rows, setRows] = useState<Communication[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [expanded, setExpanded] = useState<Set<string>>(new Set());

  const load = useCallback(async () => {
    if (!leadId && !companyId) return;
    try {
      setError(null);
      setRows(await mailApi.timeline({ leadId, companyId }));
    } catch (err) {
      setError((err as ApiError).message);
      setRows([]);
    }
  }, [leadId, companyId]);

  useEffect(() => {
    void load();
  }, [load, reloadToken]);

  const toggle = (id: string) =>
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  if (error) {
    return <div style={errorBox}>{error}</div>;
  }
  if (rows === null) {
    return <div className="empty">{t("common.loading", "Loading…")}</div>;
  }
  if (rows.length === 0) {
    return (
      <div className="empty">
        <div>{t("mail.timeline.empty", "No correspondence yet.")}</div>
        <div style={{ marginTop: 4, fontSize: 12 }}>
          {t("mail.timeline.emptyHint", "Messages you send from the portal and replies to them appear here.")}
        </div>
      </div>
    );
  }

  return (
    <ol style={list}>
      {rows.map((c) => (
        <MessageRow
          key={c.id}
          message={c}
          locale={locale}
          expanded={expanded.has(c.id)}
          onToggle={() => toggle(c.id)}
        />
      ))}
    </ol>
  );
}

function MessageRow({
  message,
  locale,
  expanded,
  onToggle,
}: {
  message: Communication;
  locale: string;
  expanded: boolean;
  onToggle: () => void;
}) {
  const { t } = useTranslation();
  const out = message.direction === "OUT";
  const failed = message.status === "FAILED";
  const body = useMemo(() => bodyOf(message), [message]);
  const long = body.length > EXCERPT_CHARS;
  const shown = expanded || !long ? body : `${body.slice(0, EXCERPT_CHARS).trimEnd()}…`;
  const stamp = message.sentAt ?? message.receivedAt ?? message.createdAt;

  // Someone else's correspondence: the backend withheld subject, body and
  // addresses. Show only that contact happened — direction, a paperclip when it
  // carried an attachment, and when.
  if (message.redacted) {
    return (
      <li style={row(out, false)}>
        <div style={rowHead}>
          <span style={directionPill(out, false)}>
            {out ? t("mail.direction.OUT", "Sent") : t("mail.direction.IN", "Received")}
          </span>
          <span style={{ ...subject, color: "var(--ink-3)", fontStyle: "italic", fontWeight: 400 }}>
            {t("mail.timeline.redacted", "Private correspondence of another user")}
          </span>
          {message.hasAttachments && (
            <span title={t("mail.timeline.hasAttachment", "Has attachment")} style={{ color: "var(--ink-3)" }}>
              <Ic.contract />
            </span>
          )}
          <span style={stampStyle} title={new Date(stamp).toLocaleString(locale)}>
            {new Date(stamp).toLocaleString(locale)}
          </span>
        </div>
      </li>
    );
  }

  return (
    <li style={row(out, failed)}>
      <div style={rowHead}>
        <span style={directionPill(out, failed)}>
          {out ? t("mail.direction.OUT", "Sent") : t("mail.direction.IN", "Received")}
        </span>
        <strong style={subject}>{message.subject || t("mail.timeline.noSubject", "(no subject)")}</strong>
        <span style={stampStyle} title={new Date(stamp).toLocaleString(locale)}>
          {new Date(stamp).toLocaleString(locale)}
        </span>
      </div>

      <div style={people}>
        <span>{message.fromEmail}</span>
        <span aria-hidden style={{ color: "var(--ink-4)" }}>→</span>
        <span>{message.toEmails}</span>
        {message.ccEmails && (
          <span style={{ color: "var(--ink-3)" }}>
            {t("mail.timeline.cc", "Cc")}: {message.ccEmails}
          </span>
        )}
        {message.sentByName && <span style={{ color: "var(--ink-3)" }}>· {message.sentByName}</span>}
      </div>

      {shown && <div style={bodyStyle}>{shown}</div>}
      {long && (
        <button type="button" className="btn ghost sm" style={{ alignSelf: "flex-start" }} onClick={onToggle}>
          {expanded ? t("mail.timeline.less", "Show less") : t("mail.timeline.more", "Show more")}
        </button>
      )}

      {(message.attachments.length > 0 || message.clickedAt || failed) && (
        <div style={chipRow}>
          {message.attachments.map((a) => (
            // Clickable: fetches a short-lived presigned URL, so the PDF that
            // actually went to the customer can be reopened from the portal.
            <button
              key={a.id}
              type="button"
              className="chip"
              title={a.filename}
              style={{ cursor: "pointer" }}
              onClick={() => void openAttachment(message.id, a.id)}
            >
              <Ic.contract />
              {a.filename}
              {a.sizeBytes != null && <span style={{ color: "var(--ink-3)" }}>· {formatSize(a.sizeBytes)}</span>}
            </button>
          ))}
          {message.clickedAt && (
            <span className="chip" style={{ borderColor: "var(--ok)", color: "var(--ok)" }}>
              <Ic.check />
              {t("mail.timeline.clicked", "Link clicked")} · {new Date(message.clickedAt).toLocaleString(locale)}
            </span>
          )}
          {failed && (
            <span className="chip" style={{ borderColor: "var(--err)", color: "var(--err)" }} title={message.error ?? ""}>
              <Ic.alert />
              {t("mail.timeline.failed", "Not delivered")}
            </span>
          )}
        </div>
      )}

      {failed && message.error && <div style={errorNote}>{message.error}</div>}
    </li>
  );
}

/** Plain-text body, falling back to a tag-stripped HTML body (ingest may carry only HTML). */
export function bodyOf(message: Pick<Communication, "bodyText" | "bodyHtml">): string {
  const text = message.bodyText?.trim();
  if (text) return text;
  const html = message.bodyHtml?.trim();
  if (!html) return "";
  return html
    .replace(/<(script|style)[\s\S]*?<\/\1>/gi, " ")
    .replace(/<br\s*\/?>/gi, "\n")
    .replace(/<\/(p|div|tr|li|h[1-6])>/gi, "\n")
    .replace(/<[^>]+>/g, "")
    .replace(/&nbsp;/g, " ")
    .replace(/&amp;/g, "&")
    .replace(/&lt;/g, "<")
    .replace(/&gt;/g, ">")
    .replace(/&quot;/g, '"')
    .replace(/&#39;/g, "'")
    .replace(/\n{3,}/g, "\n\n")
    .trim();
}

/** Human file size for an attachment chip. */
export function formatSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} kB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

export type { CommunicationAttachment };

// ---- styles (design system: var colours, chips/btn classes) -------------

const list: CSSProperties = { listStyle: "none", margin: 0, padding: 0, display: "flex", flexDirection: "column", gap: 10 };

function row(out: boolean, failed: boolean): CSSProperties {
  const rail = failed ? "var(--err)" : out ? "var(--brand-primary)" : "var(--ok)";
  return {
    display: "flex",
    flexDirection: "column",
    gap: 6,
    padding: "12px 14px",
    borderRadius: 10,
    border: "1px solid var(--border)",
    borderLeft: `3px solid ${rail}`,
    background: out ? "var(--surface-2)" : "var(--surface)",
  };
}

function directionPill(out: boolean, failed: boolean): CSSProperties {
  const color = failed ? "var(--err)" : out ? "var(--brand-primary)" : "var(--ok)";
  return {
    display: "inline-flex",
    alignItems: "center",
    padding: "2px 8px",
    borderRadius: 999,
    border: `1px solid ${color}`,
    color,
    fontSize: 11,
    fontWeight: 600,
    textTransform: "uppercase",
    letterSpacing: 0.4,
    whiteSpace: "nowrap",
  };
}

const rowHead: CSSProperties = { display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" };
const subject: CSSProperties = { fontSize: 14, color: "var(--ink)", flex: "1 1 auto", minWidth: 0 };
const stampStyle: CSSProperties = { fontSize: 12, color: "var(--ink-3)", whiteSpace: "nowrap" };
const people: CSSProperties = { display: "flex", gap: 6, flexWrap: "wrap", fontSize: 12, color: "var(--ink-2)" };
const bodyStyle: CSSProperties = { fontSize: 13, color: "var(--ink-2)", whiteSpace: "pre-wrap", wordBreak: "break-word", lineHeight: 1.5 };
const chipRow: CSSProperties = { display: "flex", gap: 6, flexWrap: "wrap", marginTop: 2 };
const errorNote: CSSProperties = { fontSize: 12, color: "var(--err)", wordBreak: "break-word" };
const errorBox: CSSProperties = { padding: 12, borderRadius: 8, background: "var(--err-bg)", color: "var(--err)", fontSize: 13 };
